// ABOUTME: Constants for heart rate zone colors used across the application
// ABOUTME: Provides consistent zone styling for charts, badges, and UI elements

export const ZONE_COLORS = {
  B: "#C0C1C5",
  0: "#8ED3FA",
  1: "#2F80ED",
  2: "#76D670",
  3: "#F5CE46",
  4: "#FE951E",
  5: "#FF307B",
  strength: "#E83A98",
} as const

export const ZONE_COLORS_BY_KEY = {
  zoneB: "#C0C1C5",
  zone0: "#8ED3FA",
  zone1: "#2F80ED",
  zone2: "#76D670",
  zone3: "#F5CE46",
  zone4: "#FE951E",
  zone5: "#FF307B",
  strength: "#E83A98",
} as const

// Tailwind background classes for zones
export const ZONE_BG_CLASSES = {
  B: "bg-[#C0C1C5]",
  0: "bg-[#8ED3FA]",
  1: "bg-[#2F80ED]",
  2: "bg-[#76D670]",
  3: "bg-[#F5CE46]",
  4: "bg-[#FE951E]",
  5: "bg-[#FF307B]",
  strength: "bg-[#E83A98]",
} as const

export type ZoneNumber = "B" | 0 | 1 | 2 | 3 | 4 | 5 | "strength"
export type ZoneKey = keyof typeof ZONE_COLORS_BY_KEY

export const getZoneColor = (zoneNumber: number | string): string => {
  const key = zoneNumber as keyof typeof ZONE_COLORS
  if (key in ZONE_COLORS) {
    return ZONE_COLORS[key]
  }
  return ZONE_COLORS[1]
}

export const getZoneBgClass = (zoneNumber: number | string): string => {
  const key = zoneNumber as keyof typeof ZONE_BG_CLASSES
  if (key in ZONE_BG_CLASSES) {
    return ZONE_BG_CLASSES[key]
  }
  return ZONE_BG_CLASSES[1]
}

// Zone order for gradient calculations (B = -1, then 0-5)
const ZONE_ORDER = ["B", 0, 1, 2, 3, 4, 5] as const

// Clarification weights within a zone (0 = begin, 0.5 = middle, 1 = end)
const CLARIFICATION_WEIGHT: Record<string, number> = {
  ZoneBegin: 0,
  ZoneMid: 0.5,
  ZoneEnd: 1,
}

// Convert zone + clarification to a numeric position on scale
const getZonePosition = (zone: number | string, clarification?: string | null): number => {
  const zoneIndex = ZONE_ORDER.indexOf(zone as (typeof ZONE_ORDER)[number])
  if (zoneIndex === -1) return 0

  const clarWeight = clarification ? (CLARIFICATION_WEIGHT[clarification] ?? 0.5) : 0.5
  // Each zone spans 1 unit, clarification adds 0-1 within that zone
  return zoneIndex + clarWeight
}

// Get gradient style for zone range with clarification support
export const getZoneGradientStyle = (
  zoneMin: number | string | null | undefined,
  zoneMax: number | string | null | undefined,
  clarificationMin?: string | null,
  clarificationMax?: string | null,
): React.CSSProperties | undefined => {
  if (zoneMin == null || zoneMax == null) return undefined

  const colorMin = getZoneColor(zoneMin)
  const colorMax = getZoneColor(zoneMax)

  // If same zone and same clarification, no gradient needed
  if (colorMin === colorMax && clarificationMin === clarificationMax) {
    return undefined
  }

  const posMin = getZonePosition(zoneMin, clarificationMin)
  const posMax = getZonePosition(zoneMax, clarificationMax)

  // If positions are very close, no gradient needed
  if (Math.abs(posMax - posMin) < 0.1) {
    return undefined
  }

  // Build gradient with all intermediate zones
  const stops: string[] = []
  const totalRange = posMax - posMin

  // Get indices of zones between min and max (inclusive)
  const minZoneIndex = ZONE_ORDER.indexOf(zoneMin as (typeof ZONE_ORDER)[number])
  const maxZoneIndex = ZONE_ORDER.indexOf(zoneMax as (typeof ZONE_ORDER)[number])

  if (minZoneIndex === -1 || maxZoneIndex === -1) {
    // Fallback to simple gradient
    return {
      background: `linear-gradient(90deg, ${colorMin} 0%, ${colorMax} 100%)`,
    }
  }

  // Add stops for each zone in the range
  for (let i = minZoneIndex; i <= maxZoneIndex; i++) {
    const zone = ZONE_ORDER[i]
    if (zone === undefined) continue
    const color = getZoneColor(zone)

    if (i === minZoneIndex) {
      // First zone: start from 0%
      stops.push(`${color} 0%`)
    } else if (i === maxZoneIndex) {
      // Last zone: end at 100%
      stops.push(`${color} 100%`)
    } else {
      // Intermediate zones: calculate position
      const zonePos = getZonePosition(zone, "ZoneMid")
      const percent = ((zonePos - posMin) / totalRange) * 100
      stops.push(`${color} ${percent.toFixed(0)}%`)
    }
  }

  return {
    background: `linear-gradient(90deg, ${stops.join(", ")})`,
  }
}
